import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { euro, formatDate, MEZZI, PREVENTIVO_VOCI } from "@/lib/constants";

const mezzoLabel = (v) => MEZZI.find((m) => m.value === v)?.label || v;

function Row({ label, value }) {
  if (!value && value !== 0) return null;
  return (
    <div className="pi-row">
      <span className="pi-label">{label}</span>
      <span className="pi-value">{value === true ? "Sì" : String(value)}</span>
    </div>
  );
}

export default function PrintItinerary() {
  const { id } = useParams();
  const [trip, setTrip] = useState(null);

  useEffect(() => {
    api.get(`/trips/${id}`).then((r) => {
      setTrip(r.data);
      setTimeout(() => window.print(), 1400);
    });
  }, [id]);

  if (!trip) return <div style={{ padding: 40 }}>Caricamento itinerario…</div>;

  const hotel = trip.hotel || {};
  const dest = trip.destinazione_lavoro || {};
  const prev = trip.preventivo || {};
  const info = trip.informazioni_consigli || {};

  const mapPoints = [];
  if (dest.indirizzo || dest.nome) mapPoints.push({ label: dest.nome || "Destinazione", addr: dest.indirizzo || dest.nome });
  if (hotel.indirizzo || hotel.struttura) mapPoints.push({ label: hotel.struttura || "Hotel", addr: hotel.indirizzo || hotel.struttura });
  (trip.parcheggi || []).forEach((p) => { if (p.indirizzo || p.nome) mapPoints.push({ label: p.nome || "Parcheggio", addr: p.indirizzo || p.nome }); });
  (trip.trasporti || []).forEach((s) => { const a = s.dettagli?.aeroporto_arrivo || s.dettagli?.aeroporto_partenza; if (a) mapPoints.push({ label: `Aeroporto ${a}`, addr: a }); });
  if (trip.destinazione && !mapPoints.some((p) => p.addr === trip.destinazione)) mapPoints.unshift({ label: "Destinazione trasferta", addr: trip.destinazione });

  return (
    <div className="print-itinerary">
      <style>{`
        .print-itinerary { max-width: 800px; margin: 0 auto; padding: 32px 40px; font-family: 'Inter', Arial, sans-serif; color: #0F172A; background:#fff; }
        .pi-topbar { height:6px; background:#E30613; border-radius:3px; margin-bottom:20px; }
        .pi-head { display:flex; justify-content:space-between; align-items:flex-start; border-bottom:2px solid #0F172A; padding-bottom:16px; margin-bottom:24px; }
        .pi-brand { font-family:'Outfit',Arial,sans-serif; font-weight:800; font-size:22px; }
        .pi-brand b { color:#E30613; }
        .pi-sub { font-size:11px; letter-spacing:2px; color:#64748B; }
        .pi-title { font-family:'Outfit',Arial,sans-serif; font-size:26px; font-weight:800; margin:0; }
        .pi-meta { font-size:13px; color:#475569; margin-top:4px; }
        .pi-section { margin-bottom:22px; page-break-inside: avoid; }
        .pi-h { font-family:'Outfit',Arial,sans-serif; font-size:15px; font-weight:700; text-transform:uppercase; letter-spacing:1px; color:#E30613; border-bottom:1px solid #E2E8F0; padding-bottom:6px; margin-bottom:10px; }
        .pi-row { display:flex; gap:12px; font-size:13px; padding:2px 0; }
        .pi-label { color:#64748B; min-width:150px; text-transform:capitalize; }
        .pi-value { color:#0F172A; font-weight:500; }
        .pi-step { border-left:2px solid #E2E8F0; padding-left:16px; margin-left:6px; }
        .pi-step-item { position:relative; margin-bottom:12px; }
        .pi-step-item:before { content:''; position:absolute; left:-23px; top:4px; width:10px; height:10px; background:#0F172A; border-radius:50%; }
        .pi-step-item.last:before { background:#E30613; }
        .pi-seg-title { font-weight:600; font-size:14px; }
        .pi-seg-sub { font-size:12px; color:#64748B; }
        .pi-total { display:flex; justify-content:space-between; font-family:'Outfit',Arial,sans-serif; font-size:18px; font-weight:800; border-top:2px solid #0F172A; padding-top:10px; margin-top:6px; }
        .pi-total .amt { color:#E30613; }
        .pi-foot { margin-top:32px; border-top:1px solid #E2E8F0; padding-top:12px; font-size:11px; color:#94A3B8; display:flex; justify-content:space-between; }
        .pi-actions { text-align:center; margin-bottom:16px; }
        .pi-btn { background:#E30613; color:#fff; border:none; padding:10px 20px; border-radius:999px; font-weight:600; cursor:pointer; font-size:14px; }
        .pi-maps { display:flex; flex-wrap:wrap; gap:12px; }
        .pi-map { display:flex; gap:10px; align-items:center; width:calc(50% - 6px); border:1px solid #E2E8F0; border-radius:8px; padding:8px; page-break-inside:avoid; box-sizing:border-box; }
        .pi-map img { width:88px; height:88px; flex-shrink:0; }
        .pi-map-label { font-weight:600; font-size:12px; }
        .pi-map-addr { font-size:11px; color:#64748B; }
        @media print { .pi-actions { display:none; } .print-itinerary { padding:12px 8px; } body { background:#fff; } }
      `}</style>

      <div className="pi-actions">
        <button className="pi-btn" onClick={() => window.print()} data-testid="print-now-btn">Stampa / Salva PDF</button>
      </div>

      <div className="pi-topbar" />
      <div className="pi-head">
        <div>
          <div className="pi-brand"><b>COSMA</b> TRAVEL DESK</div>
          <div className="pi-sub">ITINERARIO DI TRASFERTA</div>
        </div>
        <div style={{ textAlign: "right", fontSize: 12, color: "#64748B" }}>
          Emesso il {formatDate(new Date().toISOString())}
        </div>
      </div>

      <div className="pi-section">
        <h1 className="pi-title">{trip.destinazione || "Trasferta"}</h1>
        <div className="pi-meta">
          {trip.dipendente} · {trip.azienda} · {trip.motivo || ""}
        </div>
        <div className="pi-meta">
          Partenza {formatDate(trip.data_partenza)} {trip.ora_partenza} — Rientro {formatDate(trip.data_rientro)} {trip.ora_rientro}
          {trip.orario_sul_posto ? ` · Sul posto entro le ${trip.orario_sul_posto}` : ""}
        </div>
      </div>

      <div className="pi-section">
        <div className="pi-h">Programma</div>
        <div className="pi-step">
          <div className="pi-step-item">
            <div className="pi-seg-title">Partenza — {trip.destinazione}</div>
            <div className="pi-seg-sub">{formatDate(trip.data_partenza)} {trip.ora_partenza}</div>
          </div>
          {(trip.trasporti || []).map((s, i) => (
            <div className="pi-step-item" key={i}>
              <div className="pi-seg-title">{mezzoLabel(s.mezzo)}</div>
              <div className="pi-seg-sub">
                {(s.dettagli?.luogo_partenza || s.dettagli?.stazione_partenza || s.dettagli?.aeroporto_partenza || s.da || "")} → {(s.dettagli?.luogo_arrivo || s.dettagli?.stazione_arrivo || s.dettagli?.aeroporto_arrivo || s.a || "")}
                {s.dettagli?.orario_partenza ? ` · ${s.dettagli.orario_partenza}` : ""}
                {s.dettagli?.codice_prenotazione ? ` · Cod. ${s.dettagli.codice_prenotazione}` : ""}
              </div>
            </div>
          ))}
          {hotel.struttura && (
            <div className="pi-step-item">
              <div className="pi-seg-title">Hotel — {hotel.struttura}</div>
              <div className="pi-seg-sub">{hotel.indirizzo} · {hotel.numero_notti || ""} notti</div>
            </div>
          )}
          {dest.nome && (
            <div className="pi-step-item">
              <div className="pi-seg-title">Destinazione — {dest.nome}</div>
              <div className="pi-seg-sub">{dest.indirizzo} {dest.orario_appuntamento ? `· ore ${dest.orario_appuntamento}` : ""}</div>
            </div>
          )}
          {(trip.trasporti_locali || []).map((s, i) => (
            <div className="pi-step-item" key={`l${i}`}>
              <div className="pi-seg-title">Trasferimento locale — {s.mezzo}</div>
              <div className="pi-seg-sub">{s.da} → {s.a}</div>
            </div>
          ))}
          <div className="pi-step-item last">
            <div className="pi-seg-title">Rientro</div>
            <div className="pi-seg-sub">{formatDate(trip.data_rientro)} {trip.ora_rientro}</div>
          </div>
        </div>
      </div>

      {hotel.struttura && (
        <div className="pi-section">
          <div className="pi-h">Alloggio</div>
          <Row label="Struttura" value={hotel.struttura} />
          <Row label="Indirizzo" value={hotel.indirizzo} />
          <Row label="Camera" value={hotel.camera} />
          <Row label="Check-in" value={hotel.checkin} />
          <Row label="Check-out" value={hotel.checkout} />
          <Row label="Codice" value={hotel.codice_prenotazione} />
        </div>
      )}

      {(trip.prenotazioni || []).length > 0 && (
        <div className="pi-section">
          <div className="pi-h">Prenotazioni</div>
          {trip.prenotazioni.map((b, i) => (
            <div className="pi-row" key={i}>
              <span className="pi-label">{b.tipo}</span>
              <span className="pi-value">{[b.fornitore, b.codice ? `Cod. ${b.codice}` : "", formatDate(b.data), b.costo ? euro(b.costo) : ""].filter(Boolean).join(" · ")}</span>
            </div>
          ))}
        </div>
      )}

      <div className="pi-section">
        <div className="pi-h">Preventivo</div>
        {PREVENTIVO_VOCI.map((v) => (
          <div className="pi-row" key={v.key}><span className="pi-label">{v.label}</span><span className="pi-value">{euro(prev.voci?.[v.key])}</span></div>
        ))}
        <div className="pi-total"><span>Totale trasferta</span><span className="amt">{euro(prev.totale)}</span></div>
      </div>

      {Object.values(info).some(Boolean) && (
        <div className="pi-section">
          <div className="pi-h">Informazioni e consigli</div>
          {Object.entries(info).filter(([, v]) => v).map(([k, v]) => (
            <Row key={k} label={k.replace(/_/g, " ")} value={v} />
          ))}
        </div>
      )}

      {mapPoints.length > 0 && (
        <div className="pi-section">
          <div className="pi-h">Mappe e indirizzi — scansiona il QR per aprire la mappa</div>
          <div className="pi-maps">
            {mapPoints.map((p) => {
              const url = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(p.addr)}`;
              const qr = `https://api.qrserver.com/v1/create-qr-code/?size=160x160&margin=1&data=${encodeURIComponent(url)}`;
              return (
                <div className="pi-map" key={p.label}>
                  <img src={qr} alt={p.label} crossOrigin="anonymous" />
                  <div className="pi-map-info"><div className="pi-map-label">{p.label}</div><div className="pi-map-addr">{p.addr}</div></div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="pi-foot">
        <span>COSMA TRAVEL DESK — Documento interno</span>
        <span>{trip.destinazione} · {formatDate(trip.data_partenza)}</span>
      </div>
    </div>
  );
}
